import {
  AGENT_MONITOR,
  ONECLOUD_MONITOR,
  VMWARE_MONITOR,
  OTHER_MONITOR,
  SANGFOR_MONITOR,
} from '@Compute/views/vminstance/constants'
import { POD_MONITOR } from '@Compute/views/vminstance-container/constants'
import { CONTAINER_MONITOR } from '@Compute/views/pod-container/constants'
import { HOST_AGENT_MONITOR } from '@Monitor/views/monitorresource/constants'
import { RDS_MONITOR_ALL_OPTS, REDIS_MONITOR_ALL_OPTS } from '@DB/constants'
import { STORAGE_MONITOR_OPTS } from '@Storage/views/blockstorage/constants'
import { OSS_MONITOR_OPTS } from '@Storage/constants'
import i18n from '@/locales'
import { HYPERVISORS_MAP } from '@/constants'
import {
  MONITOR_TEMPLATE_VMINSTANCE,
  MONITOR_TEMPLATE_HOST,
  MONITOR_TEMPLATE_VMINSTANCE_CONTAINER,
  MONITOR_TEMPLATE_CONTAINER,
  MONITOR_TEMPLATE_RDS,
  MONITOR_TEMPLATE_REDIS,
  MONITOR_TEMPLATE_BLOCKSTORAGE,
  MONITOR_TEMPLATE_BUCKET,
  MONITOR_TEMPLATE_BAREMETAL,
  resolvePanelMonitorType,
} from '@Monitor/utils/monitorTemplate'

export const RES_TYPE_OPTIONS = [
  { key: 'guest', label: i18n.t('dictionary.server') },
]

export const MONITOR_TYPE_OPTIONS = [
  { key: 'basic', label: i18n.t('compute.monitor.basic') },
  { key: 'agent', label: i18n.t('compute.monitor.agent') },
]

// 平台名称统一取 HYPERVISORS_MAP.label：kvm 为内置虚拟化（跟随 OEM 品牌），sangfor 等走平台翻译
export const HYPERVISOR_FILTER_OPTIONS = ['kvm', 'esxi', 'sangfor'].map(key => ({
  key,
  label: (HYPERVISORS_MAP[key] && HYPERVISORS_MAP[key].label) || key,
}))

// 基础监控来源：同一指标会出现在多个平台，按指标聚合后合并使用场景
const BASIC_MONITOR_SOURCES = [
  { scenario: 'onecloud', panels: ONECLOUD_MONITOR, hypervisors: ['kvm'] },
  { scenario: 'vmware', panels: VMWARE_MONITOR, hypervisors: ['esxi'] },
  { scenario: 'sangfor', panels: SANGFOR_MONITOR, hypervisors: ['sangfor'] },
  // 其它平台（非 KVM/VMware/深信服）的默认监控，作为通用场景
  { scenario: 'general', panels: OTHER_MONITOR, hypervisors: [] },
]

const SCENARIO_ORDER = ['onecloud', 'vmware', 'sangfor', 'general']
// 场景与平台 hypervisor 的对应关系，展示名称统一走平台翻译
const SCENARIO_HYPERVISOR = {
  onecloud: 'kvm',
  vmware: 'esxi',
  sangfor: 'sangfor',
}
// 聚合后按监控类别顺序展示，便于阅读
const CATEGORY_ORDER = ['cpu', 'mem', 'disk', 'netio', 'diskio']

function scenarioLabel (scenario) {
  if (scenario === 'general') return i18n.t('monitor.resource_template.scenario.general')
  const hypervisor = SCENARIO_HYPERVISOR[scenario] || scenario
  const item = HYPERVISORS_MAP[hypervisor]
  return (item && item.label) || hypervisor
}

function categoryIndex (panel) {
  const index = CATEGORY_ORDER.indexOf(panel.category)
  return index === -1 ? CATEGORY_ORDER.length : index
}

export function panelKey (panel) {
  return `${panel.res_type || 'guest'}|${panel.fromItem}|${panel.seleteItem}`
}

// 使用场景合并展示，如：推荐 内置虚拟化、VMware、通用 场景使用
export function formatRecommendTip (scenarios = []) {
  const labels = SCENARIO_ORDER
    .filter(key => scenarios.includes(key))
    .map(key => scenarioLabel(key))
  if (!labels.length) return i18n.t('monitor.resource_template.recommend.agent')
  const scenario = labels.join(i18n.t('monitor.resource_template.scenario_separator'))
  return i18n.t('monitor.resource_template.recommend_usage', [scenario])
}

function mergeBasicCatalog () {
  const merged = new Map()
  BASIC_MONITOR_SOURCES.forEach(({ scenario, panels, hypervisors }) => {
    (panels || []).forEach(panel => {
      const key = `${panel.fromItem}.${panel.seleteItem}`
      const exist = merged.get(key)
      if (exist) {
        if (!exist.scenarios.includes(scenario)) exist.scenarios.push(scenario)
        hypervisors.forEach(item => {
          if (!exist.recommend_hypervisors.includes(item)) exist.recommend_hypervisors.push(item)
        })
        return
      }
      merged.set(key, {
        ...panel,
        category: panel.name || 'other',
        monitor_type: 'basic',
        res_type: 'guest',
        scenarios: [scenario],
        recommend_hypervisors: [...hypervisors],
      })
    })
  })
  return [...merged.values()]
    .sort((a, b) => categoryIndex(a) - categoryIndex(b))
    .map(item => ({
      ...item,
      recommend_tip: formatRecommendTip(item.scenarios),
    }))
}

function getAgentCatalog () {
  return (AGENT_MONITOR || []).map(panel => ({
    ...panel,
    category: panel.name || 'other',
    monitor_type: 'agent',
    // Agent 指标归属 Agent 资源类型，便于再次编辑时回填正确
    res_type: 'agent',
    scenarios: ['agent'],
    recommend_hypervisors: [],
    recommend_tip: formatRecommendTip(['agent']),
  }))
}

// 宿主机 / 容器主机 / 容器 / RDS / Redis / 块存储 / 存储桶 / 裸金属的内置监控面板（与虚拟机不同，没有平台差异）
const FLAT_CATALOG_PANELS = {
  [MONITOR_TEMPLATE_HOST]: HOST_AGENT_MONITOR,
  [MONITOR_TEMPLATE_VMINSTANCE_CONTAINER]: POD_MONITOR,
  [MONITOR_TEMPLATE_CONTAINER]: CONTAINER_MONITOR,
  [MONITOR_TEMPLATE_RDS]: RDS_MONITOR_ALL_OPTS,
  [MONITOR_TEMPLATE_REDIS]: REDIS_MONITOR_ALL_OPTS,
  [MONITOR_TEMPLATE_BLOCKSTORAGE]: STORAGE_MONITOR_OPTS,
  // 存储桶指标按云平台区分，合并后按测量项去重
  [MONITOR_TEMPLATE_BUCKET]: Object.keys(OSS_MONITOR_OPTS).reduce((ret, brand) => ret.concat(OSS_MONITOR_OPTS[brand]), []),
  // 裸金属只有 Agent 监控，指标与虚拟机 Agent 一致
  [MONITOR_TEMPLATE_BAREMETAL]: AGENT_MONITOR,
}

export function isFlatTemplateCategory (category) {
  return !!FLAT_CATALOG_PANELS[category]
}

// RDS / Redis / 存储桶的指标按云平台区分，推荐列表按「测量项.字段」去重后展示
function uniqueCatalogPanels (panels) {
  const seen = {}
  return (panels || []).filter(panel => {
    const key = `${panel.fromItem}.${panel.seleteItem}`
    if (seen[key]) return false
    seen[key] = true
    return true
  })
}

function buildContainerCatalog (panels) {
  return uniqueCatalogPanels(panels).map(panel => ({
    ...panel,
    category: panel.name || 'other',
    // 裸金属等资源只有 Agent 指标，按测量项前缀区分监控类型
    monitor_type: resolvePanelMonitorType(panel.fromItem, panel.res_type),
    // res_type 由页面按 measurements 推断，避免写死错误的资源类型
    scenarios: ['general'],
    recommend_hypervisors: [],
    recommend_tip: formatRecommendTip(['general']),
  }))
}

// 虚拟机内置监控指标推荐目录
export function getGuestRecommendCatalog ({ monitorType = 'basic', hypervisor, brand } = {}) {
  const list = monitorType === 'agent' ? getAgentCatalog() : mergeBasicCatalog()

  return list.filter(item => {
    if (hypervisor && item.recommend_hypervisors?.length && !item.recommend_hypervisors.includes(hypervisor)) {
      return false
    }
    if (brand && item.recommend_brands?.length) {
      const brands = item.recommend_brands.map(b => String(b).toLowerCase())
      if (!brands.includes(String(brand).toLowerCase())) return false
    }
    return true
  })
}

// 按策略类型获取内置监控指标：宿主机/容器主机/容器只有基础监控，虚拟机区分基础与 Agent 监控
export function getRecommendCatalog ({ category = MONITOR_TEMPLATE_VMINSTANCE, hypervisor, brand } = {}) {
  const panels = FLAT_CATALOG_PANELS[category]
  if (panels) return buildContainerCatalog(panels)
  return [
    ...getGuestRecommendCatalog({ monitorType: 'basic', hypervisor, brand }),
    ...getGuestRecommendCatalog({ monitorType: 'agent', hypervisor, brand }),
  ]
}
